/**
 * useMonerisToken Hook
 *
 * Manages the full lifecycle of Moneris Hosted Tokenization.
 * Validation errors (invalid card, expiry, CVD) come FROM the Moneris iframe
 * via postMessage response codes — we do NOT implement our own card validation.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { MONERIS_ORIGIN, MONERIS_RESPONSE_CODES } from '../constants/monerisConfig';

export interface TokenState {
  dataKey: string | null;
  bin: string | null;
  isTokenizing: boolean;
  fieldErrors: Record<string, string>;
  isReady: boolean;
}

interface FieldErrorMapping {
  field: string;
  message: string;
}

const FIELD_ERROR_MAP: Record<string, FieldErrorMapping> = {
  [MONERIS_RESPONSE_CODES.INVALID_CARD_DATA]: {
    field: 'cardNumber',
    message: 'Card number is invalid',
  },
  [MONERIS_RESPONSE_CODES.INVALID_EXPIRY]: {
    field: 'expiry',
    message: 'Expiry date is invalid',
  },
  [MONERIS_RESPONSE_CODES.INVALID_CVD]: {
    field: 'cvd',
    message: 'CVD is invalid',
  },
  [MONERIS_RESPONSE_CODES.INVALID_PROFILE_ID]: {
    field: 'general',
    message: 'Payment configuration error. Please contact support.',
  },
  [MONERIS_RESPONSE_CODES.TOKEN_GENERATION_ERROR]: {
    field: 'general',
    message: 'Unable to process card. Please try again.',
  },
  [MONERIS_RESPONSE_CODES.INVALID_PROFILE_OR_SOURCE]: {
    field: 'general',
    message: 'Payment configuration error. Please contact support.',
  },
};

export function useMonerisToken() {
  const [tokenState, setTokenState] = useState<TokenState>({
    dataKey: null,
    bin: null,
    isTokenizing: false,
    fieldErrors: {},
    isReady: false,
  });

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const setIframeRef = useCallback((iframe: HTMLIFrameElement) => {
    iframeRef.current = iframe;
  }, []);

  const requestToken = useCallback(() => {
    console.log('[Moneris] Pay Now clicked — requesting token from iframe...');
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) {
      console.error('[Moneris] ERROR: iframe ref not available');
      setTokenState((prev) => ({
        ...prev,
        fieldErrors: { general: 'Payment form is not ready. Please wait and try again.' },
      }));
      return;
    }

    setTokenState((prev) => ({
      ...prev,
      isTokenizing: true,
      fieldErrors: {},
      dataKey: null,
      bin: null,
    }));

    console.log('[Moneris] Sending postMessage("tokenize") to iframe, origin:', MONERIS_ORIGIN);
    iframe.contentWindow.postMessage('tokenize', MONERIS_ORIGIN);
  }, []);

  const resetToken = useCallback(() => {
    setTokenState({
      dataKey: null,
      bin: null,
      isTokenizing: false,
      fieldErrors: {},
      isReady: true,
    });
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== MONERIS_ORIGIN) {
        return;
      }

      console.log('[Moneris] Received postMessage from iframe, origin:', event.origin);
      console.log('[Moneris] Raw response data:', event.data);

      let respData: Record<string, unknown>;
      try {
        respData = typeof event.data === 'string'
          ? JSON.parse(event.data)
          : event.data;
      } catch {
        console.error('[Moneris] ERROR: Failed to parse response data');
        return;
      }

      const { responseCode, dataKey, errorMessage, bin } = respData as {
        responseCode: string | string[];
        dataKey?: string;
        errorMessage?: string;
        bin?: string;
      };
      console.log('[Moneris] Parsed response — responseCode:', responseCode, '| dataKey:', dataKey, '| bin:', bin, '| errorMessage:', errorMessage);

      const codes = Array.isArray(responseCode) ? responseCode : [responseCode];
      const isSuccess = codes.every((code) => code === MONERIS_RESPONSE_CODES.SUCCESS);

      if (isSuccess && dataKey) {
        console.log('[Moneris] SUCCESS — Token received:', dataKey);
        console.log('[Moneris] Card BIN:', bin);
        setTokenState({
          dataKey,
          bin: bin || null,
          isTokenizing: false,
          fieldErrors: {},
          isReady: true,
        });
      } else {
        console.warn('[Moneris] VALIDATION FAILED — Error codes:', codes);
        const fieldErrors: Record<string, string> = {};
        codes
          .filter((code) => code !== MONERIS_RESPONSE_CODES.SUCCESS)
          .forEach((code) => {
            const mapped = FIELD_ERROR_MAP[code];
            if (mapped) {
              fieldErrors[mapped.field] = mapped.message;
            } else {
              fieldErrors.general = (errorMessage as string) || 'An unexpected error occurred.';
            }
          });

        setTokenState((prev) => ({
          ...prev,
          dataKey: null,
          bin: null,
          isTokenizing: false,
          fieldErrors,
          isReady: true,
        }));
      }
    }

    window.addEventListener('message', handleMessage, false);
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  return {
    tokenState,
    requestToken,
    resetToken,
    setIframeRef,
  };
}
